import keyBy from 'lodash/keyBy';

import {lookupTablesClient} from 'common/report/queries/app-api/lookup-tables-client';
import {PropertyType, ResourceType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {RawLookupTable} from 'common/report/util/data-definitions/types';

import {eventPropertiesClient} from '../queries/query-api/event-properties-client';
import {Store} from './store';

export type LookupTableProperty = {name: string; label: string; type: PropertyType; count: number};
type AugmentedLookupTable = Omit<RawLookupTable, 'properties'> & {
  tempMappings: {
    event: Set<string>;
    user: Set<string>;
    'event-custom-properties': Set<number>;
    'user-custom-properties': Set<number>;
  };
  properties: LookupTableProperty[];
};
export interface LookupTables {
  [id: string]: AugmentedLookupTable;
}

const lookupTableProperties = new Map<string, LookupTableProperty[]>();

export const lookupTablesStore = new Store<LookupTables>({
  apiClient: lookupTablesClient,
  defaultValue: {},
  name: `lookup-tables`,
  transform: {
    index: (response: RawLookupTable[]) => {
      const tables = response.map((table) => {
        return {
          ...table,
          tempMappings: {
            event: new Set(),
            user: new Set(),
            'event-custom-properties': new Set(),
            'user-custom-properties': new Set(),
          },
          properties: lookupTableProperties.get(table.id) || [],
        };
      });
      return keyBy(tables, `id`);
    },
  },
});

export type LookupTablesStore = typeof lookupTablesStore;

/** Fetch a lookup table's properties and update that table in the store.
    Result is cached and subsequent calls for the same lookup table are aborted */
export async function fetchLookupTableProperties(dataGroupId, projectId, workspaceId) {
  if (lookupTableProperties.get(dataGroupId)) {
    return;
  }
  lookupTableProperties.set(dataGroupId, []);
  const params = {
    dataGroupId,
    includeDimensionProperties: true,
    projectId,
    workspaceId,
  };
  try {
    const properties = await eventPropertiesClient.query(params);
    lookupTableProperties.set(dataGroupId, properties);
    lookupTablesStore.update({
      [dataGroupId]: {
        ...lookupTablesStore.value[dataGroupId],
        properties,
      },
    });
  } catch (error) {
    reporter.error(error, `[lookup] Error loading lookup table properties`, {dataGroupId, projectId, workspaceId});
  }
}

/** Registers a temporary [property -> lookup table] mapping.
 * PropertyItemWithValue conforms to the first argument's interface.
 */
export function registerTempLookupTableMapping(
  property: {value: string | string[]; resourceType: ResourceType; customPropertyId?: number | string},
  dataGroupId: string,
): {succeeded: true} | {succeeded: false; errorMessage: string} {
  const resourceType = property.resourceType;
  const custom = Boolean(property.customPropertyId);
  // value: string property name for regular properties, numeric id for custom properties
  let value: string | number;
  if (custom) {
    value = property.customPropertyId;
  } else {
    value = Array.isArray(property.value) ? property.value[0] : property.value;
  }

  const existingDataGroupId = getTempLookupTableMappings(resourceType, custom).get(value);
  if (existingDataGroupId) {
    if (existingDataGroupId === dataGroupId) {
      return {succeeded: true};
    } else {
      reporter.error(`[lookup] Cannot add temporary mapping: property already temporarily mapped`, {
        existingDataGroupId,
        dataGroupId,
        propertyValue: value,
      });
      return {
        succeeded: false,
        errorMessage: `Could not apply temporary mapping: property is already temporarily mapped`,
      };
    }
  }
  const table = lookupTablesStore.value[dataGroupId];
  if (!table) {
    reporter.error(`[lookup] Cannot add temporary mapping: lookup table not available in store`, {dataGroupId});
    return {succeeded: false, errorMessage: `Error applying the temporary mapping`};
  }

  const resourceKey = getResourceKey(resourceType, custom);
  const tempMappings = {...table.tempMappings, [resourceKey]: new Set([...table.tempMappings[resourceKey], value])};
  lookupTablesStore.update({
    [dataGroupId]: {
      ...table,
      tempMappings,
    },
  });
  fetchLookupTableProperties(dataGroupId, getProjectId(), getWorkspaceId());
  return {succeeded: true};
}

/** Unegisters a temporary [property -> lookup table] mapping.
 * PropertyItemWithValue conforms to the argument's interface.
 */
export function unregisterTempLookupTableMapping(property: {
  value: string | string[];
  resourceType: ResourceType;
  customPropertyId?: number | string;
}) {
  const resourceType = property.resourceType;
  const propertyName = Array.isArray(property.value) ? property.value[0] : property.value;
  const custom = Boolean(property.customPropertyId);
  const value = custom ? property.customPropertyId : propertyName;
  const dataGroupId = getTempLookupTableMappings(resourceType, custom).get(value);
  if (!dataGroupId) {
    reporter.error(`[lookup] Cannot find temporary mapping to remove`, {resourceType, value});
    return false;
  }
  const table = lookupTablesStore.value[dataGroupId];
  const resourceKey = getResourceKey(resourceType, custom);
  const newMappedProperties = [...table.tempMappings[resourceKey]].filter((prop) => prop !== value);
  const tempMappings = {...table.tempMappings, [resourceKey]: new Set(newMappedProperties)};
  lookupTablesStore.update({
    [dataGroupId]: {
      ...table,
      tempMappings,
    },
  });
  return true;
}

/** returns a Map of (propertyName | customPropertyId) -> dataGroupId mappings. a property can currently only be mapped to a single data group id */
export function getTempLookupTableMappings(resourceType: ResourceType, custom = false) {
  if (![ResourceType.Event, ResourceType.User].includes(resourceType)) {
    return new Map();
  }

  const resourceKey = getResourceKey(resourceType, custom);
  const mappings: Map<string | number, string> = new Map();
  for (const table of Object.values(lookupTablesStore.value)) {
    for (const value of table.tempMappings[resourceKey]) {
      mappings.set(value, table.id);
    }
  }
  return mappings;
}

/** Returns the lookup table id (dataGroupId) if a temporary mapping exists, else undefined */
export function getTempLookupTableId(property: {
  value: string | string[];
  resourceType: ResourceType;
  customPropertyId?: number | string;
}): string | undefined {
  const resourceType = property.resourceType;
  const propertyName = Array.isArray(property.value) ? property.value[0] : property.value;
  const custom = Boolean(property.customPropertyId);
  const value = custom ? property.customPropertyId : propertyName;
  return getTempLookupTableMappings(resourceType, custom).get(value);
}

function getResourceKey(resourceType: ResourceType, custom: boolean) {
  return custom ? `${resourceType}-custom-properties` : resourceType;
}
